import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  GlobalStoreService,
  PractitionerSelectorComponent,
} from '@principle-theorem/ng-principle-shared';
import { NgDialogModule, TypedFormControl } from '@principle-theorem/ng-shared';
import {
  IHicapsConnectMerchant,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  FieldValue,
  Firestore,
  WithRef,
} from '@principle-theorem/shared';
import { HICAPS_SETTINGS_NOTICE_MS } from '../hicaps-connect-v2-settings-notice';

export interface IHicapsV2EditMerchantRequest {
  merchant: WithRef<IHicapsConnectMerchant>;
}

@Component({
  selector: 'pr-hicaps-connect-v2-edit-merchant-dialog',
  templateUrl: './hicaps-connect-v2-edit-merchant-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgDialogModule, PractitionerSelectorComponent],
})
export class HicapsConnectV2EditMerchantDialogComponent {
  private readonly _dialogRef =
    inject<MatDialogRef<HicapsConnectV2EditMerchantDialogComponent>>(
      MatDialogRef
    );
  private readonly _snackBar = inject(MatSnackBar);
  private _practitionerRef: DocumentReference<IStaffer> | undefined;

  readonly data = inject<IHicapsV2EditMerchantRequest>(MAT_DIALOG_DATA);
  readonly globalStore = inject(GlobalStoreService);
  readonly nameControl = new TypedFormControl<string>(
    this.data.merchant.name ?? ''
  );

  constructor() {
    this._practitionerRef = this.data.merchant.practitionerRef;
  }

  practitionerSelected(
    practitionerRef: DocumentReference<IStaffer> | undefined
  ): void {
    this._practitionerRef = practitionerRef;
  }

  async save(): Promise<void> {
    try {
      await Firestore.patchDoc(this.data.merchant.ref, {
        name: this._resolveName(),
        practitionerRef: this._practitionerRef ?? FieldValue.delete(),
      });
    } catch {
      this._snackBar.open('Save failed', 'OK', {
        duration: HICAPS_SETTINGS_NOTICE_MS,
      });
      return;
    }
    this._dialogRef.close();
  }

  /**
   * `name` is the user-owned nickname and `providerName` is refreshed by sync.
   * Clearing the field rather than writing an empty string keeps the merchant
   * falling back to the synced provider name.
   */
  private _resolveName(): string | FieldValue {
    const name = this.nameControl.value?.trim() ?? '';
    return name.length ? name : FieldValue.delete();
  }
}
